import React, { useContext, useEffect, useState } from 'react';

import {
  Grid,
  Typography,
} from '@material-ui/core';

import {
  useHistory,
  useParams,
  withRouter,
} from 'react-router-dom';

import PropTypes from 'prop-types';

import AddButton from '../../buttons/addButton';
import AddProgramsDialog from '../../modals/addProgramsDialog';
import AlertDialog from '../../modals/alertDialog';
import BreadCrumb from '../../fields/breadCrumb';
import ClinicianDetailsView from './clinicianDetailsView';
import ClinicianProgramsList from './clinicianProgramsList';
import CustomSnackbar from '../../feedback/customSnackbar';
import EditButton from '../../buttons/editButton';
import Footer from '../../fields/footer';
import Header from '../../fields/header';
import MenuDrawer from '../../fields/menuDrawer';
import SpinnerLoading from '../../feedback/spinnerLoading';

import { getClinicianInfo, getClinicianPrograms, getPrograms, removeProgram } from '../../../utils/comms';

import { extractAdministeredProgramsId } from '../../../utils/helpers';

import translations from '../../../languages/i18n';

import generalStyles from '../../../styles/generalStyles';
import { sectionTitle } from '../../../styles/formElementsStyles';

// NOTA: Per poder emprar el react-router-dom i enllaçar pàgines s'ha de convertir el component en una funció i exportar-lo embolicat per withRouter
// En cas de que el component sigui un fill directe de Route no fa falta fer-ho
// Explicat al punt 2.1. Using withRouter de https://dev.to/projectescape/programmatic-navigation-in-react-3p1l

import { SessionContext } from '../../../utils/context';

function ClinicianInfo({ lang, handleChangeLang }) {
  const sessionContext = useContext(SessionContext);

  const classes = generalStyles();
  const sectionTitleStyle = sectionTitle();

  const [administeredProgramsList, setAdministeredProgramsList] = useState([]);
  const [clinicianData, setClinicianData] = useState({});
  const [clinicianPrograms, setClinicianPrograms] = useState({});
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('');
  const [infoReady, setInfoReady] = useState(false);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [showAddPrograms, setShowAddPrograms] = useState(false);
  const [showRemoveAlert, setShowRemoveAlert] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showLoading, setShowLoading] = useState(false);

  const history = useHistory();
  const { idClinician } = useParams();

  useEffect(() => {
    const getClinicianInfoFromServer = async () => {
      setShowLoading(true);
      const responseClinicianInfo = await Promise.all([
        getClinicianInfo(idClinician, sessionContext.userToken),
        getClinicianPrograms(idClinician, sessionContext.userToken),
        getPrograms(sessionContext.userToken), // Demanam els programes de l'usuari que maneja la interfície web. Si és l'Administrador del sistema, obtendrà tots els programes del sistema
      ]);
      setShowLoading(false);
      // Comprovam si dins les respostes n'hi ha alguna de timeOut o erronia
      const allOk = responseClinicianInfo.map((answer) => {
        if (answer.status && answer.status === 200) return 'ok';
        if (answer === 'timeOut') return 'timeOut';
        return 'error';
      });
      const idTO = allOk.indexOf('timeOut');
      const idE = allOk.indexOf('error');
      if (idTO > -1 || idE > -1) { // Al manco hi ha 1 timeOut o 1 error
        // Miram si algun dels errors rebuts és un timeOut
        if (idTO > -1) { // Hi ha hagut un timeOut
          console.log('hi ha hagut 1 error de TimeOut');
        } else { // Hi ha hagut un error
          console.log('hi ha hagut 1 error de client o servidor');
        }
      } else { // No hi ha cap error
        sessionContext.userData = responseClinicianInfo[0].data.clinician;
        setClinicianData(responseClinicianInfo[0].data.clinician);
        setClinicianPrograms(responseClinicianInfo[1].data.programs);
        // Agafam els ids dels programes administrats per l'usuari clínic que accedeix a la web
        // ATENCIÓ: si l'usuari és l'administrador del sistema, dins administeredPrograms hi ha un array buit
        const administeredPrograms = extractAdministeredProgramsId(responseClinicianInfo[2].data.programs, sessionContext.userId);
        setAdministeredProgramsList(administeredPrograms);
        setInfoReady(true);
      }
    };
    sessionContext.userData = null; // Buidam les dades per només guardar les darreres obtingudes del servidor
    getClinicianInfoFromServer();
  }, []);

  const editClinician = () => {
    history.push(`/${sessionContext.role.toLowerCase()}/editClinicianInfo/${idClinician}`);
  };

  const editProgram = (idProgram) => {
    history.push(`/${sessionContext.role.toLowerCase()}/editProgramInfo/${idProgram}`);
  };

  const obtainClinicianPrograms = async () => {
    const response = await getClinicianPrograms(idClinician, sessionContext.userToken);
    setShowLoading(false);
    if (response.status === 200) { // Tenim llistat
      setClinicianPrograms(response.data.programs);
    } else if (response === 400) { // Error amb les dades
      console.log('Error 400');
    } else { // Login KO. Altres errors
      console.log(response);
    }
  };

  const gotoScreen = (screen) => {
    history.push(`/${sessionContext.role.toLowerCase()}/${screen}`);
  };

  const manageAddProgramsAnswer = (reloadPrograms) => {
    setShowAddPrograms(false);
    if (reloadPrograms) {
      obtainClinicianPrograms();
    }
  };

  const manageFeedback = (fbType, fbMessage) => {
    setFeedbackMessage(translations[`_${fbType}${fbMessage}`]);
    setFeedbackType(fbType);
    setShowFeedback(true);
  };

  const removePrograms = async () => {
    setShowLoading(true);
    // Esborram usuaris
    const responseDelete = await Promise.all(selectedPrograms.map((element) => removeProgram(idClinician, element.id, sessionContext.userToken)));
    // Comprovam si dins les respostes n'hi ha alguna de timeOut, de conflicte o erronia
    const allOk = responseDelete.map((answer) => {
      if (answer.status && answer.status === 200) return 'ok';
      if (answer === 'timeOut') return 'timeOut';
      if (answer === 409) return 'conflict';
      return 'error';
    });
    const idTO = allOk.indexOf('timeOut');
    const idC = allOk.indexOf('conflict');
    const idE = allOk.indexOf('error');
    if (idTO > -1 || idC > -1 || idE > -1) { // Al manco hi ha 1 timeOut, 1 conflicte o 1 error
      // Miram si algun dels errors rebuts és un timeOut
      if (idTO > -1) { // Hi ha hagut un timeOut
        manageFeedback('error', 'TimeOut');
      } else if (idC > -1) { // Hi ha hagut un conflicte
        manageFeedback('error', 'RemoveProgramConflict');
      } else { // Hi ha hagut un error
        manageFeedback('error', 'RemoveProgram');
      }
    } else { // No hi ha cap error
      manageFeedback('success', 'RemoveProgram');
    }
    setSelectedPrograms([]);
    // Obtenim llistat actualitzat dels programes
    obtainClinicianPrograms();
  };

  const manageRemoveAlertAnswer = (agree) => {
    setShowRemoveAlert(false);
    if (agree) removePrograms();
  };

  const selectedProgram = (id, descriptiveName) => {
    const selectedProgramsCopy = selectedPrograms.slice();
    // Cercam si dins selectedProgramsCopy hi ha l'identificador del programa
    const idx = selectedProgramsCopy.findIndex((item) => item.id === id);
    if (idx < 0) selectedProgramsCopy.push({ id, descriptiveName }); // Si idx és -1 vol dir que no ha trobat el valor, l'afegim a la taula
    else selectedProgramsCopy.splice(idx, 1); // L'ha trobat l'eliminam de la taula
    setSelectedPrograms(selectedProgramsCopy);
  };

  const showMenuDrawer = (show) => {
    setShowDrawer(show);
  };

  return (
    <div className={classes.content}>
      <SpinnerLoading show={showLoading} />
      <Header type={sessionContext.role.toLowerCase()} lang={lang} handleChangeLang={handleChangeLang} handleShowDrawer={(show) => showMenuDrawer(show)} />
      <MenuDrawer type={sessionContext.role.toLowerCase()} showDrawer={showDrawer} handleShowDrawer={(show) => showMenuDrawer(show)} handleGotoScreen={(screen) => gotoScreen(screen)} />
      <AlertDialog open={showRemoveAlert} handleClose={(agree) => manageRemoveAlertAnswer(agree)} title={translations['_removeProgramsAlertTitle']} message={translations['_removeProgramsAlertMessage']} data={selectedPrograms} field='descriptiveName' />
      <CustomSnackbar show={showFeedback} handleCloseFeedback={(show) => setShowFeedback(show)} type={feedbackType} message={(showFeedback && feedbackMessage) || ''} />
      {/* Pos showAddPrograms && per evitar que s'executi el useEffect de dins AddProgramsDialog fins que no obro el AddPrograms */}
      {showAddPrograms && <AddProgramsDialog open={showAddPrograms} clinicianPrograms={clinicianPrograms} idUser={idClinician} handleClose={(reloadPrograms) => manageAddProgramsAnswer(reloadPrograms)} handleFeedback={(fbType, fbMessage) => manageFeedback(fbType, fbMessage)} title={translations['_addProgramsDialogTitle']} />}
      <div className={classes.body}>
        <Grid container direction='column' spacing={2}>
          {/* ----------------------- Fila 0 ----------------------- */}
          <Grid item xs={12}>
            <BreadCrumb role={sessionContext.role.toLowerCase()} home='listClinicians' lang={lang} />
          </Grid>
          {/* ----------------------- Fila 1 ----------------------- */}
          <Grid item className={sectionTitleStyle.root}>
            <Grid container justify='space-between' spacing={2}>
              <Grid item>
                <Typography variant='h4' component='div' className={sectionTitleStyle.title}>{translations['_clinician']}</Typography>
              </Grid>
              <Grid item>
                {sessionContext.role === 'Admin' && <EditButton handleClick={() => editClinician()} />}
              </Grid>
            </Grid>
          </Grid>
          {/* ----------------------- Fila 2 ----------------------- */}
          <Grid item>
            <Grid container justify='space-between' spacing={2}>
              <Grid item xs={6}>
                <ClinicianDetailsView clinicianData={clinicianData} />
              </Grid>
              <Grid item xs={6} />
            </Grid>
          </Grid>
          {/* ----------------------- Fila 3 ----------------------- */}
          <Grid item xs={12}>
            <AddButton disabled={false} handleClick={() => setShowAddPrograms(true)} />
          </Grid>
          {/* ----------------------- Fila 4 ----------------------- */}
          <Grid item>
            <Grid container justify='space-between' spacing={2}>
              <Grid item xs={12}>
                {infoReady && (
                  <ClinicianProgramsList
                    handleRemove={() => setShowRemoveAlert(true)}
                    handleEditProgram={(program) => editProgram(program)}
                    handleSelectedProgram={(id, descriptiveName) => selectedProgram(id, descriptiveName)}
                    administeredPrograms={administeredProgramsList}
                    programsData={clinicianPrograms}
                    selectedPrograms={selectedPrograms}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <Footer />
    </div>
  );
}

export default withRouter(ClinicianInfo);

// Indica i controla els props que se han de passar al component
ClinicianInfo.propTypes = {
  handleChangeLang: PropTypes.func.isRequired,
  lang: PropTypes.string.isRequired,
};
